import {
    Bell,
    Key,
    Loader2,
    Moon,
    Sun,
    User,
} from 'lucide-react';
import React, { useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../components/ui/alert.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { Separator } from '../components/ui/separator.tsx';
import { Switch } from '../components/ui/switch.tsx';
import { useTheme } from '../contexts/theme-provider.tsx';

interface Settings {
  email: string;
  notifications: {
    emailDigest: boolean;
    urlVisits: boolean;
    security: boolean;
  };
  apiKey: string;
}

const Settings = () => {
  const { theme, setTheme } = useTheme();
  const [settings, setSettings] = useState<Settings>({
    email: 'user@example.com',
    notifications: {
      emailDigest: true,
      urlVisits: true,
      security: true,
    },
    apiKey: 'sk_test_123456789',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  const handleNotificationChange = (key: keyof Settings['notifications']) => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // API call to update email would go here
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSuccess('Email updated successfully');
    } catch (err) {
      setError('Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const regenerateApiKey = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // API call to regenerate API key would go here
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSettings((prev) => ({
        ...prev,
        apiKey: 'sk_test_' + Math.random().toString(36).substring(2),
      }));
      setSuccess('API key regenerated successfully');
    } catch (err) {
      setError('Failed to regenerate API key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>

          {/* Theme Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {theme === 'dark' ? (
                  <Moon className="h-5 w-5" />
                ) : (
                  <Sun className="h-5 w-5" />
                )}
                Theme
              </CardTitle>
              <CardDescription>
                Choose your preferred theme
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label htmlFor="theme-toggle">Dark mode</Label>
                <Switch
                  id="theme-toggle"
                  checked={theme === 'dark'}
                  onCheckedChange={(checked) =>
                    setTheme(checked ? 'dark' : 'light')
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Settings
              </CardTitle>
              <CardDescription>
                Update your account information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={settings.email}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                    />
                    <Button type="submit" disabled={loading}>
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>
                Choose what notifications you want to receive
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Weekly Email Digest</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive a weekly summary of your URL statistics
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.emailDigest}
                  onCheckedChange={() => handleNotificationChange('emailDigest')}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>URL Visit Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your URLs receive significant traffic
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.urlVisits}
                  onCheckedChange={() => handleNotificationChange('urlVisits')}
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Security Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive notifications about security-related events
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.security}
                  onCheckedChange={() => handleNotificationChange('security')}
                />
              </div>
            </CardContent>
          </Card>

          {/* API Key Management */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Key Management
              </CardTitle>
              <CardDescription>
                Manage your API key for programmatic access
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Your API Key</Label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.apiKey}
                    readOnly
                    className="font-mono"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? 'Hide' : 'Show'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={regenerateApiKey}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Regenerate'
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Keep this key secure. Don't share it with anyone.
                </p>
              </div>
            </CardContent>
          </Card>

          {success && (
            <Alert>
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
